import { Component, OnInit, signal, computed } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
	combineLatest,
	debounceTime,
	delay,
	filter,
	map,
	Observable,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
	getStateUniversity,
	getStateUniversityRatingStats,
} from '../../data-access/+state/university.selectors';
import {
	fetchUniversity,
	fetchUniversityRatingStats,
} from '../../data-access/+state/university.actions';
import { UniversityReviewsApiService } from '../../data-access/services/university-reviews-api.service';
import {
	QueryParamsState,
	ReviewsSortBy,
	ReviewsSortByUnion,
} from '../../models/university-reviews.models';
import * as SEO from '../../utils/seo-metadata.utils';
import {
	UniversityReviews,
	UniversityReview,
	UniversityProfile,
	Gallery,
} from '@uc/web/shared/data-models';
import { QueryParamsStatesService } from '../../services/query-params-state.service';
import {
	UrlModifierService,
	SortService,
	SortComponent,
	SortOptionModel,
} from '@uc/web/shared/features/feature-filter';
import { SeoMetadataService, isStoreEmpty } from '@uc/utilities';
import { NoResultComponent, SearchBarService, SearchComponent } from '@uc/shared/ui';
import { ClientFeatureHandler, UniversityReadUrlService } from '@uc/web/core';
import { UniversitySkeletonComponent } from '../../components/university-skeleton/university-skeleton.component';
import { AsyncPipe } from '@angular/common';
import { UniversitySliderComponent } from '@uc/web/shared/features/feature-university-slider';
import { UniversityHeaderTitleComponent } from '../../components/university-header-title/university-header-title.component';
import { UniversityReviewsStatsComponent } from '../../components/university-reviews-stats/university-reviews-stats.component';
import { UniversityReviewsOverviewComponent } from '../../components/university-reviews-overview/university-reviews-overview.component';
import { ReviewCardComponent } from '../../components/review-card/review-card.component';
import {
	WriteReviewBtnComponent,
	LoadMoreBtnComponent,
	LoadMoreBtnService,
} from '@uc/web/shared/buttons';
import {
	NullResultsGAService,
	LoadMoreGAService,
	UniProfileGAEventDirective,
	UniImpressionDirective,
} from '@uc/web/analytics';
import { Product, ProductEnum } from '@uc/shared/data-models';
import { ClientProfileStickyLeadBarMobileComponent } from '@web/shared/features/feature-sticky-lead-bar';

@Component({
	selector: 'uc-university-reviews',
	templateUrl: './university-reviews.component.html',
	imports: [
		AsyncPipe,
		UniversitySkeletonComponent,
		UniversityHeaderTitleComponent,
		UniversityReviewsStatsComponent,
		UniversityReviewsOverviewComponent,
		ReviewCardComponent,
		SearchComponent,
		NoResultComponent,
		WriteReviewBtnComponent,
		LoadMoreBtnComponent,
		ClientProfileStickyLeadBarMobileComponent,
		SortComponent,
		UniversitySliderComponent,
		UniProfileGAEventDirective,
		UniImpressionDirective,
	],
	providers: [UniversityReviewsApiService, UrlModifierService],
})
export class UniversityReviewsComponent implements OnInit {
	universityReviews!: UniversityReviews;
	singleReview!: UniversityReview;
	university$!: Observable<UniversityProfile | null>;
	uniGallery!: Gallery;

	page = 1;
	merge = false;
	product: Product = ProductEnum.Undergraduate;

	queryParams = this._route.snapshot.queryParams;
	reviewId = this.queryParams['reviewId'];
	universityUrl = this._route.parent?.snapshot.params['uniName'];
	isPreview = this.queryParams['preview'] === 'true';
	baseUrl = `/universities/${this.universityUrl}/reviews`;

	displaySingleReview = signal(false);
	searchTerm = signal('');
	isLoadMore = signal(false);
	isLoading = signal(true);
	firstLoad = signal(true);
	filterInteraction = signal(false);

	showInitialLoader = computed(
		() =>
			this.isLoading() &&
			this.firstLoad() &&
			!this.isLoadMore() &&
			!this.filterInteraction(),
	);

	showContentLoader = computed(() => this.isLoading() && !this.isLoadMore());

	canLoadMore = computed(
		() =>
			(this.isLoadMore() || (!this.isLoading() && !this.isLoadMore())) &&
			!this.displaySingleReview(),
	);

	sortOptions = this._queryParamsStateSrv.sortOptions;
	universityRatingStats$ = this._store.pipe(select(getStateUniversityRatingStats));
	clientFeatures!: ClientFeatureHandler;
	defaultSortOptions: SortOptionModel = {
		id: ReviewsSortBy.HighestRanked,
		text: 'Highest ranked',
	};

	universityReviews$ = combineLatest([
		this._handleLoadMore(),
		this._handleSearchInput(),
		this._handleSortChange(),
	]).pipe(
		switchMap(() => this._queryParamsStateSrv.queryParamsState$),
		tap(() => this.isLoading.set(true)),
		switchMap((queryParams) => this._getReviews(queryParams)),
		map((reviews) => this._processReviews(reviews)),
		tap((reviews) => {
			if ('data' in reviews && reviews.data && reviews.data.length === 0) {
				this._nullResultsGASrv.nullResults(this.searchTerm());
			}
			this.merge = false;
		}),
	);

	constructor(
		private _route: ActivatedRoute,
		private _store: Store,
		private _seoSrv: SeoMetadataService,
		private _searchSrv: SearchBarService,
		private _sortSrv: SortService,
		private _reviewsSrv: UniversityReviewsApiService,
		private _queryParamsStateSrv: QueryParamsStatesService,
		private _loadMoreSrv: LoadMoreBtnService,
		private _loadMoreGASrv: LoadMoreGAService,
		private _routeUtils: UrlModifierService,
		private _readUrlSrv: UniversityReadUrlService,
		private _nullResultsGASrv: NullResultsGAService,
	) {}

	ngOnInit(): void {
		this._store.dispatch(
			fetchUniversity({
				universityUrl: this.universityUrl,
				product: 'undergraduate',
			}),
		);
		this._store.dispatch(
			fetchUniversityRatingStats({
				universityUrl: this.universityUrl,
			}),
		);

		// on page load reset the query params state
		this._queryParamsStateSrv.resetQueryParamsState();

		// selecting the sort default option
		this._sortSrv.setDefaultOption(this.defaultSortOptions);

		this.displaySingleReview.set(this.reviewId ? true : false);

		const searchTerm = this.queryParams['s'];
		const page = this.queryParams['page'];
		this.page = page ? +page : 1;
		this.searchTerm.set(searchTerm ? searchTerm : '');

		// set the state with university name from the url
		this._queryParamsStateSrv.updateQueryParamsState({
			university: this.universityUrl,
			contains: searchTerm ? searchTerm : '',
			page: this.page ? this.page : 1,
			offset: this.page && this.page > 1 ? 0 : 1,
		});

		this.university$ = this._store.select(getStateUniversity).pipe(
			delay(1),
			filter((university) => !isStoreEmpty(university)),
			tap((university) => university && this._handleUniversityProfile(university)),
		);
	}

	getNoReviewsMessage(reviewsCount: number, universityName: string): string {
		return reviewsCount === 0
			? `${universityName} has no reviews yet`
			: reviewsCount === 1
				? `${universityName} has one review`
				: `It looks like your search didn't bring back any results`;
	}

	private _getReviews(
		queryParams: Partial<QueryParamsState> & Pick<QueryParamsState, 'university'>,
	) {
		return this.reviewId
			? this._reviewsSrv.getUniversityReview(+this.reviewId)
			: this._reviewsSrv.getUniversityReviews(queryParams);
	}

	private _handleSEO(universityName: string): void {
		this._seoSrv.updateTitle(SEO.generateTitle(universityName, 'reviews'));
		this._seoSrv.updateDescription(
			SEO.generateDescription(universityName, 'reviews'),
		);
	}

	private _setProduct(): void {
		this.product =
			this.clientFeatures.postgraduate && !this.clientFeatures.undergraduate
				? ProductEnum.Postgraduate
				: ProductEnum.Undergraduate;
	}

	private _handleLoadMore() {
		const initialPage = Number(this._route.snapshot.queryParams['page'] || 1);
		return this._loadMoreSrv.getLoadMorePage$.pipe(
			startWith({ page: initialPage }),
			tap(({ page }) => {
				if (this.firstLoad()) return;

				if (page > 1) {
					this._routeUtils.appendToUrl('page', page, this.baseUrl);
					this._loadMoreGASrv.loadResults(this.searchTerm());
				}

				this.page = page;
				this.merge = true;
				this.isLoadMore.set(true);

				this._queryParamsStateSrv.updateQueryParamsState({ page, offset: 1 });
			}),
		);
	}

	private _handleSearchInput() {
		return this._searchSrv.inputValue$.pipe(
			tap((searchTerm) => {
				this.searchTerm.set(searchTerm || this.searchTerm());
			}),
			debounceTime(500),
			tap(() => {
				if (this.firstLoad()) return;

				this.page = 1;
				this.filterInteraction.set(true);

				this._routeUtils.appendToUrl('s', this.searchTerm(), this.baseUrl);
				this._routeUtils.appendToUrl('page', this.page, this.baseUrl);

				this._queryParamsStateSrv.updateQueryParamsState({
					contains: this.searchTerm(),
					page: this.page,
				});
			}),
		);
	}

	private _handleSortChange() {
		return this._sortSrv.selectedOption$.pipe(
			startWith(this.defaultSortOptions),
			tap((sortOption) => {
				if (this.firstLoad()) return;

				this.page = 1;
				this.filterInteraction.set(true);

				this._routeUtils.appendToUrl('page', this.page, this.baseUrl);

				this._queryParamsStateSrv.updateQueryParamsState({
					sortBy: sortOption.id as ReviewsSortByUnion,
					page: this.page,
				});
			}),
		);
	}

	private _processReviews(
		response: UniversityReviews | { universityReview: UniversityReview },
	): UniversityReviews {
		this.isLoading.set(false);
		this.isLoadMore.set(false);
		this.firstLoad.set(false);
		this.reviewId = null;
		this.filterInteraction.set(false);

		this._routeUtils.appendToUrl('reviewId', '', this.baseUrl);

		if ('data' in response) {
			this.displaySingleReview.set(false);

			if (this.merge === false) {
				this.universityReviews = response;
				return response;
			}

			const appendUniversitiesReviews: UniversityReviews = {
				...response,
				data: [...this.universityReviews.data, ...response.data],
			};
			this.universityReviews = appendUniversitiesReviews;
			return appendUniversitiesReviews;
		} else {
			this.displaySingleReview.set(true);
			this.singleReview = response.universityReview;
			this.searchTerm.set(this.singleReview.title);
			return {} as UniversityReviews;
		}
	}

	private _handleUniversityProfile(university: UniversityProfile) {
		this.clientFeatures = new ClientFeatureHandler(
			university.paid_features,
			this.isPreview,
		);

		this._setProduct();
		this._readUrlSrv.setProductManually(this.product);
		this._handleSEO(university.name);

		this.uniGallery = {
			cover_image: university.undergraduate.cover_image,
			images: university.universityGallery?.images,
		};
	}
}
